import { useEffect, useState } from 'react'
import {
  Building2,
  ArrowUp,
  Trash2,
  Loader2,
  Plus,
} from 'lucide-react'
import { api } from '../../services/api'
import { useGameStore } from '../../store/gameStore'
import { useAuthStore } from '../../store/authStore'
import type { Structure, StructureType, ResourceAmount } from '@territory-nexus/shared'
import { STRUCTURE_COSTS, STRUCTURE_META, STRUCTURE_EFFECTS, RESOURCE_CONFIG, RESOURCE_ORDER, LEVEL_CAPS, canAffordCost, getZoneName } from '@territory-nexus/shared'
import clsx from 'clsx'
import { StructureSprite } from './sprites'

interface ZoneStructures {
  zone: { id: string; hexId: string }
  structures: Structure[]
}

export default function StructuresPanel() {
  const { playerZones, resources, fetchResources, fetchPlayerZones } = useGameStore()
  const [zoneStructures, setZoneStructures] = useState<ZoneStructures[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  // Fetch structures for all player zones
  const fetchAllStructures = async () => {
    try {
      setLoading(true)
      setError(null)

      const structureData: ZoneStructures[] = await Promise.all(
        playerZones.map(async (zone) => {
          try {
            const res = await api.get<{ success: boolean; data: Structure[] }>(`/structures/zone/${zone.id}`)
            return { zone, structures: res.data.success ? res.data.data : [] }
          } catch {
            return { zone, structures: [] }
          }
        })
      )

      setZoneStructures(structureData)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to load structures')
    } finally {
      setLoading(false)
    }
  }

  const zoneIds = playerZones.map(z => z.id).join(',')

  useEffect(() => {
    fetchPlayerZones()
    fetchResources()
  }, [])

  useEffect(() => {
    if (playerZones.length > 0) {
      fetchAllStructures()
      // Auto-select first zone if none selected or selected zone no longer exists
      if (!selectedZoneId || !playerZones.find(z => z.id === selectedZoneId)) {
        setSelectedZoneId(playerZones[0].id)
      }
    } else {
      setLoading(false)
    }
  }, [zoneIds])

  const UPGRADE_COST_MULTIPLIER = 1.5
  const getUpgradeCost = (structureType: StructureType, currentLevel: number): ResourceAmount => {
    const baseCost = STRUCTURE_COSTS[structureType]
    const multiplier = Math.pow(UPGRADE_COST_MULTIPLIER, currentLevel)
    return {
      energy: Math.floor(baseCost.energy * multiplier),
      nanomaterials: Math.floor(baseCost.nanomaterials * multiplier),
      quantum: Math.floor(baseCost.quantum * multiplier),
      darkMatter: Math.floor(baseCost.darkMatter * multiplier),
    }
  }

  const canAfford = (cost: ResourceAmount) => {
    if (!resources) return false
    return canAffordCost(resources as ResourceAmount, cost)
  }

  const getDemolishRefund = (structureType: StructureType, currentLevel: number): ResourceAmount => {
    const baseCost = STRUCTURE_COSTS[structureType]
    const total: ResourceAmount = { energy: 0, nanomaterials: 0, quantum: 0, darkMatter: 0 }
    for (let lv = 1; lv <= currentLevel; lv++) {
      const mult = Math.pow(UPGRADE_COST_MULTIPLIER, lv - 1)
      total.energy += Math.floor(baseCost.energy * mult)
      total.nanomaterials += Math.floor(baseCost.nanomaterials * mult)
      total.quantum += Math.floor(baseCost.quantum * mult)
      total.darkMatter += Math.floor(baseCost.darkMatter * mult)
    }
    return {
      energy: Math.floor(total.energy * 0.5),
      nanomaterials: Math.floor(total.nanomaterials * 0.5),
      quantum: Math.floor(total.quantum * 0.5),
      darkMatter: Math.floor(total.darkMatter * 0.5),
    }
  }

  const getEffectDescription = (structureType: StructureType, level: number): string => {
    const effect = STRUCTURE_EFFECTS[structureType]
    if (effect && 'description' in effect) {
      return effect.description(level)
    }
    return ''
  }

  const handleBuild = async (zoneId: string, structureType: StructureType) => {
    setActionLoading(`build-${zoneId}-${structureType}`)
    try {
      await api.post('/structures/build', { zoneId, structureType })
      await Promise.all([fetchAllStructures(), fetchResources()])
      useAuthStore.getState().refreshPlayer()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to build structure')
      setTimeout(() => setError(null), 3000)
    } finally {
      setActionLoading(null)
    }
  }

  const handleUpgrade = async (structureId: string) => {
    setActionLoading(`upgrade-${structureId}`)
    try {
      await api.post(`/structures/${structureId}/upgrade`)
      await Promise.all([fetchAllStructures(), fetchResources()])
      useAuthStore.getState().refreshPlayer()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to upgrade structure')
      setTimeout(() => setError(null), 3000)
    } finally {
      setActionLoading(null)
    }
  }

  const handleDemolish = async (structureId: string, refundAmount?: ResourceAmount) => {
    const refundText = refundAmount
      ? `\n\nYou will receive 50% refund: ${RESOURCE_ORDER.filter(k => refundAmount[k] > 0).map(k => `${RESOURCE_CONFIG[k].symbol}${refundAmount[k]}`).join(' ')}`
      : ''
    if (!confirm(`Are you sure you want to demolish this structure?${refundText}`)) return

    setActionLoading(`demolish-${structureId}`)
    try {
      await api.delete(`/structures/${structureId}`)
      await Promise.all([fetchAllStructures(), fetchResources()])
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to demolish structure')
      setTimeout(() => setError(null), 3000)
    } finally {
      setActionLoading(null)
    }
  }

  const getAvailableStructures = (existingStructures: Structure[]): StructureType[] => {
    const existingTypes = new Set(existingStructures.map(s => s.structureType))
    return (Object.keys(STRUCTURE_META) as StructureType[]).filter(type => !existingTypes.has(type))
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    )
  }

  const selectedData = zoneStructures.find(zs => zs.zone.id === selectedZoneId)
  const selectedStructures = selectedData?.structures || []
  const availableStructures = getAvailableStructures(selectedStructures)

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 pb-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-display font-bold text-white">Structures</h2>
          <span className="text-sm text-slate-400">{playerZones.length} zones</span>
        </div>

        {error && (
          <div className="glass border border-red-500/30 bg-red-500/10 rounded-lg p-3 mb-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
      </div>

      {playerZones.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="glass rounded-xl p-8 text-center border border-white/5">
            <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 mb-2">No zones owned</p>
            <p className="text-slate-500 text-sm">Claim zones from the map to build structures</p>
          </div>
        </div>
      ) : (
        <>
          {/* Zone selector — horizontal scroll pills */}
          <div className="px-4 pb-3">
            <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-1">
              {zoneStructures.map(({ zone, structures }) => {
                const isSelected = zone.id === selectedZoneId
                return (
                  <button
                    key={zone.id}
                    onClick={() => setSelectedZoneId(zone.id)}
                    className={clsx(
                      'shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border',
                      isSelected
                        ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400'
                        : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-slate-300'
                    )}
                  >
                    <span className="whitespace-nowrap">{getZoneName(zone.hexId)}</span>
                    <span className={clsx(
                      'ml-1.5 text-[10px]',
                      isSelected ? 'text-emerald-500/70' : 'text-slate-600'
                    )}>
                      {structures.length}/{Object.keys(STRUCTURE_META).length}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Selected zone content */}
          {selectedData && (
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              <div className="max-w-2xl mx-auto space-y-3">

                {/* Existing structures */}
                {selectedStructures.length > 0 && (
                  <div className="space-y-2">
                    {selectedStructures.map((structure) => {
                      const info = STRUCTURE_META[structure.structureType]
                      const isLoading = actionLoading?.startsWith(`upgrade-${structure.id}`) ||
                                       actionLoading?.startsWith(`demolish-${structure.id}`)
                      const isMaxLevel = structure.level >= LEVEL_CAPS.structure
                      const upgradeCost = !isMaxLevel ? getUpgradeCost(structure.structureType, structure.level) : null
                      const canAffordUpgrade = upgradeCost ? canAfford(upgradeCost) : false
                      const effectDesc = getEffectDescription(structure.structureType, structure.level)
                      const nextEffectDesc = !isMaxLevel ? getEffectDescription(structure.structureType, structure.level + 1) : null
                      const refund = structure.structureType !== 'headquarters' ? getDemolishRefund(structure.structureType, structure.level) : null

                      return (
                        <div
                          key={structure.id}
                          className="glass rounded-lg border border-white/5 overflow-hidden flex gap-4 p-3"
                        >
                          {/* Left: Large sprite */}
                          <div className="shrink-0 flex items-center">
                            <StructureSprite
                              structureType={structure.structureType}
                              level={structure.level}
                              size="xl"
                              showLevel={false}
                            />
                          </div>

                          {/* Right: Info + actions */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-base font-semibold text-white leading-tight">{info?.shortName}</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  Lv {structure.level}{isMaxLevel ? ' (MAX)' : `/${LEVEL_CAPS.structure}`}
                                </p>
                              </div>
                              {structure.structureType !== 'headquarters' && refund && (
                                <button
                                  onClick={() => handleDemolish(structure.id, refund)}
                                  disabled={isLoading}
                                  className="p-1 rounded-md text-red-400/40 hover:text-red-400 hover:bg-red-500/10 transition disabled:opacity-50 shrink-0"
                                  title="Demolish"
                                >
                                  {actionLoading === `demolish-${structure.id}` ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              )}
                            </div>

                            {effectDesc && (
                              <p className="text-xs text-slate-400 mt-1.5">{effectDesc}</p>
                            )}

                            {!isMaxLevel && (
                              <div className="flex items-center gap-2 mt-2">
                                <button
                                  onClick={() => canAffordUpgrade && handleUpgrade(structure.id)}
                                  disabled={isLoading || !canAffordUpgrade}
                                  className={clsx(
                                    'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition disabled:opacity-50',
                                    canAffordUpgrade
                                      ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                                      : 'bg-white/5 text-slate-600 cursor-not-allowed'
                                  )}
                                >
                                  {actionLoading === `upgrade-${structure.id}` ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <ArrowUp className="w-3.5 h-3.5" />
                                  )}
                                  Upgrade
                                </button>
                                {upgradeCost && (
                                  <span className="flex items-center gap-1.5 text-xs">
                                    {RESOURCE_ORDER.map((resKey) => {
                                      if (!upgradeCost[resKey] || upgradeCost[resKey] <= 0) return null
                                      const rc = RESOURCE_CONFIG[resKey]
                                      return (
                                        <span key={resKey} className={clsx('flex items-center gap-0.5', canAffordUpgrade ? rc.color : 'text-red-400')}>
                                          {rc.symbol}{upgradeCost[resKey]}
                                        </span>
                                      )
                                    })}
                                  </span>
                                )}
                              </div>
                            )}
                            {nextEffectDesc && (
                              <p className="text-[11px] text-slate-600 mt-1">{nextEffectDesc}</p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Build new structures */}
                {availableStructures.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Build New</p>
                    <div className="grid grid-cols-1 gap-2">
                      {availableStructures.map((type) => {
                        const info = STRUCTURE_META[type]
                        const cost = STRUCTURE_COSTS[type]
                        const affordable = canAfford(cost)
                        const isBuilding = actionLoading === `build-${selectedZoneId}-${type}`
                        const lv1Effect = getEffectDescription(type, 1)

                        return (
                          <div
                            key={type}
                            className={clsx(
                              'rounded-lg border transition overflow-hidden',
                              affordable
                                ? 'bg-white/5 border-white/10'
                                : 'bg-slate-900/60 border-white/5'
                            )}
                          >
                            <div className="flex gap-3 p-3 pb-2">
                              <div className={clsx('shrink-0', !affordable && 'grayscale opacity-40')}>
                                <StructureSprite
                                  structureType={type}
                                  level={1}
                                  size="lg"
                                  showLevel={false}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={clsx('text-sm font-semibold', affordable ? 'text-white' : 'text-slate-500')}>{info?.name}</p>
                                <p className={clsx('text-xs mt-0.5 leading-relaxed', affordable ? 'text-slate-400' : 'text-slate-600')}>{info?.description}</p>
                                {lv1Effect && (
                                  <p className={clsx('text-[11px] mt-1', affordable ? info?.color : 'text-slate-600')}>
                                    Lv 1: {lv1Effect}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-white/5 bg-white/[0.02]">
                              <div className="flex items-center gap-2 flex-wrap">
                                {RESOURCE_ORDER.map((resKey) => {
                                  if (cost[resKey] <= 0) return null
                                  const rc = RESOURCE_CONFIG[resKey]
                                  const playerAmount = resKey === 'energy' ? resources.energy
                                    : resKey === 'nanomaterials' ? resources.nanomaterials
                                    : resKey === 'quantum' ? resources.quantum
                                    : resources.darkMatter
                                  const hasEnough = playerAmount >= cost[resKey]
                                  return (
                                    <span key={resKey} className={clsx('text-xs font-medium flex items-center gap-0.5', hasEnough ? rc.color : 'text-red-400')}>
                                      {rc.symbol}{cost[resKey]}
                                    </span>
                                  )
                                })}
                              </div>
                              <button
                                onClick={() => affordable && handleBuild(selectedZoneId!, type)}
                                disabled={!affordable || isBuilding}
                                className={clsx(
                                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition shrink-0',
                                  affordable
                                    ? 'bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25 border border-cyan-500/30'
                                    : 'bg-white/5 text-slate-600 cursor-not-allowed border border-white/5'
                                )}
                              >
                                {isBuilding ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Plus className="w-3.5 h-3.5" />
                                )}
                                Build
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {selectedStructures.length === 0 && availableStructures.length === 0 && (
                  <div className="glass rounded-xl p-6 text-center border border-white/5">
                    <p className="text-slate-500 text-sm">No structures available</p>
                  </div>
                )}

                {selectedStructures.length > 0 && availableStructures.length === 0 && (
                  <p className="text-xs text-slate-500 text-center py-2">All structures built</p>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
